import { Currency, SgCurrencyPickerCustomEvent } from '@sg-bootstrap/components';
import { useCallback, useEffect, useRef } from 'react';

type UseCurrencyPicker = { currency: string; setCurrency: (currency: string) => void };
export function useCurrencyPicker({ currency, setCurrency }: UseCurrencyPicker) {
  const currencyRef = useRef<HTMLSgCurrencyPickerElement>(undefined);

  // Write currency value into hidden input generated by sg-currency-picker (bypasses React)
  // the web component never writes to  the input itself. so we do it manually
  // dispatchEvent+ true only on real user selection to keep Matomo from analytics state accurate
  const syncCurrencyPickerValueWithInputValue = useCallback((value: string, dispatchEvent = false) => {
    const webComponent = currencyRef.current;
    if (!webComponent) {
      return;
    }

    const input = webComponent.parentElement?.querySelector('input');
    if (!input) {
      return;
    }

    const nativeInputSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
    nativeInputSetter?.call(input, value);

    if (dispatchEvent) {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }, []);

  useEffect(() => {
    const webComponent = currencyRef.current;
    if (!webComponent) {
      return;
    }
    // sg-currency-picker wre-renders rest the hidden input value to empty string ""
    // the observer restores the current  currency value after each re-render without dispatching
    // a change event to avoid duplicate Matomo interaction
    const observer = new MutationObserver(() => syncCurrencyPickerValueWithInputValue(currency));
    observer.observe(webComponent, { subtree: true, childList: true, attributes: true });

    return () => observer.disconnect();
  }, [currency, syncCurrencyPickerValueWithInputValue]);

  useEffect(() => {
    currencyRef.current?.selectItems(currency);
  }, [currency]);

  useEffect(() => {
    const selectItem: EventListener = event => {
      const customEvent = event as SgCurrencyPickerCustomEvent<{ items: Currency[] }>;
      const selectedCurrency = customEvent.detail?.items?.[0]?.id;
      // Skip if the selected currency is the same as the current one cause
      // currency picker fires event even if the selection didn't change
      if (selectedCurrency === currency) {
        return;
      }
      const value = customEvent.detail?.items?.[0]?.id;
      setCurrency(value);
      // syncCurrencyPickerValueWithInputValue(value, true);
    };
    currencyRef.current?.addEventListener('itemsSelected', selectItem);
    const currentCurrencyRef = currencyRef.current;
    return () => currentCurrencyRef?.removeEventListener('itemsSelected', selectItem);
  }, [setCurrency, syncCurrencyPickerValueWithInputValue, currency]);

  return { currencyRef };
}
